import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, useMapEvents, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import useSupercluster from 'use-supercluster';

import Strings from '../../Strings';
import { DeviceTypes } from '../../utils/deviceTypes';

const CENTER_OF_US_COORDS = [41.850033, -87.6500523];
const CLUSTER_RADIUS = 30;

export const calculateSignalColor = signalLevel => {
  return signalLevel === 'Weak'
    ? '#DA547D'
    : signalLevel === 'Medium'
      ? '#F9A452'
      : signalLevel === 'Good' || signalLevel === 'Excellent'
        ? '#4EB067'
        : '#DA547D';
};

// Component to handle map events and bounds updates
function MapEventsHandler({ onBoundsChange, onZoomChange }) {
  const map = useMapEvents({
    zoomend: () => {
      onZoomChange(map.getZoom());
      onBoundsChange(map.getBounds());
    },
    moveend: () => {
      onBoundsChange(map.getBounds());
    },
  });

  useEffect(() => {
    // Set initial bounds
    onBoundsChange(map.getBounds());
    onZoomChange(map.getZoom());
  }, []);

  return null;
}

MapEventsHandler.propTypes = {
  onBoundsChange: PropTypes.func.isRequired,
  onZoomChange: PropTypes.func.isRequired,
};

// Component to fit bounds when markers change
function FitBoundsHandler({ markers }) {
  const map = useMap();

  useEffect(() => {
    if (markers && markers.length > 1) {
      const bounds = markers.map(m => [m.properties.location.lat, m.properties.location.lng]);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [markers, map]);

  return null;
}

FitBoundsHandler.propTypes = {
  markers: PropTypes.array,
};

function MapGraph(props) {
  const { markers, maxZoomLevel, onClick } = props;
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(markers && markers.length === 1 ? 9 : 4);
  const mapRef = useRef(null);

  const handleBoundsChange = useCallback(newBounds => {
    setBounds(newBounds);
  }, []);

  const handleZoomChange = useCallback(newZoom => {
    setZoom(newZoom);
  }, []);

  const getWorstSignal = pointMarkers => {
    const hubsSignal = pointMarkers.map(h => h.properties.signalStrength);

    if (hubsSignal.includes('Weak')) return 'Weak';
    if (hubsSignal.includes('Medium')) return 'Medium';
    if (hubsSignal.includes('Good')) return 'Good';
    if (hubsSignal.includes('Excellent')) return 'Excellent';
    if (hubsSignal.includes('Not available')) return 'Not available';
    return undefined;
  };

  const getColorForCluster = (sc, cluster) => {
    const clusterMarkers = sc.getLeaves(cluster.id);
    const clusterSignal = getWorstSignal(clusterMarkers);
    return calculateSignalColor(clusterSignal);
  };

  const superclusterBounds = useMemo(() => {
    if (!bounds) return [-180, -85, 180, 85];
    return [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ];
  }, [bounds]);

  const { clusters, supercluster } = useSupercluster({
    points: markers || [],
    bounds: superclusterBounds,
    zoom,
    options: { maxZoom: maxZoomLevel, radius: CLUSTER_RADIUS },
  });

  const handleClusterClick = (sc, cluster) => {
    const isMaxZoom = zoom >= maxZoomLevel;

    if (isMaxZoom) {
      // At max zoom, the popup will show the cluster details
      return;
    }

    if (mapRef.current) {
      const expansionZoom = Math.min(
        sc.getClusterExpansionZoom(cluster.id),
        maxZoomLevel
      );
      mapRef.current.setView(
        [cluster.geometry.coordinates[1], cluster.geometry.coordinates[0]],
        expansionZoom
      );
    }
  };

  const initialCenter = useMemo(() => {
    if (markers && markers.length === 1) {
      return [markers[0].properties.location.lat, markers[0].properties.location.lng];
    }
    return CENTER_OF_US_COORDS;
  }, [markers]);

  const initialZoom = markers && markers.length === 1 ? 9 : 4;

  return (
    <MapContainer
      center={initialCenter}
      zoom={initialZoom}
      maxZoom={maxZoomLevel}
      style={{ height: '100%', width: '100%' }}
      ref={mapRef}
      zoomControl
      attributionControl
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />
      <MapEventsHandler onBoundsChange={handleBoundsChange} onZoomChange={handleZoomChange} />
      <FitBoundsHandler markers={markers} />

      {clusters.map(cluster => {
        const [longitude, latitude] = cluster.geometry.coordinates;
        const { cluster: isCluster, point_count: pointCount } = cluster.properties;

        if (isCluster) {
          const color = getColorForCluster(supercluster, cluster);
          return (
            <CircleMarker
              key={`cluster-${cluster.id}`}
              center={[latitude, longitude]}
              radius={12}
              pathOptions={{
                color,
                fillColor: color,
                fillOpacity: 1,
                weight: 1,
              }}
              eventHandlers={{
                click: () => handleClusterClick(supercluster, cluster),
              }}
            >
              <>
                <Tooltip>{`${pointCount} ${Strings.devicesInThisArea}. ${zoom >= maxZoomLevel ? 'Click to see list.' : 'Click to zoom in.'}`}</Tooltip>
                <Popup>
                  <div>
                    <h3>{`${pointCount} ${Strings.devicesInThisArea}`}</h3>
                    {zoom >= maxZoomLevel && (
                      <SimpleBar style={{ maxHeight: 210 }}>
                        {supercluster.getLeaves(cluster.id, Infinity).map(h => (
                          <div key={`hubsInArea_${h.properties.id}`} className="legendContainer left">
                            <div
                              className={
                                h.properties.signalStrength === 'Weak'
                                  ? 'dot low'
                                  : h.properties.signalStrength === 'Medium'
                                    ? 'dot medium'
                                    : h.properties.signalStrength === 'Good' || h.properties.signalStrength === 'Excellent'
                                      ? 'dot high'
                                      : 'dot low'
                              }
                            />
                            <a href={`/devices/${DeviceTypes.Datahub.id}/${h.properties.id}`} className="label">
                              {h.properties.id}
                            </a>
                          </div>
                        ))}
                      </SimpleBar>
                    )}
                  </div>
                </Popup>
              </>
            </CircleMarker>
          );
        }

        return (
          <CircleMarker
            key={`marker-${cluster.properties.id}`}
            center={[latitude, longitude]}
            radius={8}
            pathOptions={{
              color: cluster.properties.color,
              fillColor: cluster.properties.color,
              fillOpacity: 1,
              weight: 1,
            }}
            eventHandlers={{
              click: () => onClick(cluster),
            }}
          >
            <Tooltip>
              <div className="legendContainer left">
                <div
                  className={
                    cluster.properties.signalStrength === 'Weak'
                      ? 'dot low'
                      : cluster.properties.signalStrength === 'Medium'
                        ? 'dot medium'
                        : cluster.properties.signalStrength === 'Good' || cluster.properties.signalStrength === 'Excellent'
                          ? 'dot high'
                          : 'dot low'
                  }
                />
                <div>
                  {`${cluster.properties.id}. Click to navigate to device page.`}
                </div>
              </div>
            </Tooltip>
          </CircleMarker>
        );
      })}
    </MapContainer>
  );
}

MapGraph.propTypes = {
  markers: PropTypes.array,
  maxZoomLevel: PropTypes.number,
  onClick: PropTypes.func,
};

export default React.memo(MapGraph);
